import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Option({ on, onClick, title, sub, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} data-on={on ? "1" : "0"}
      className={`w-full border-2 px-3 py-2 text-left text-xs transition-colors duration-100 ${on ? "border-black bg-black text-white" : "border-zinc-300 bg-white hover:border-black"}`}>
      <b className="block">{title}</b>
      <span className={on ? "text-zinc-300" : "text-zinc-500"}>{sub}</span>
    </button>
  );
}

export const pauseSummary = (p) => [
  p.scope === "buyer" ? "Whole buyer paused" : "Delivery place paused",
  p.rerouted ? `${p.rerouted} waiting lead${p.rerouted === 1 ? "" : "s"} sent to the next buyer` : null,
  p.held ? `${p.held} held until you resume` : null,
].filter(Boolean).join(" · ");

// One-tap pause for a Down alert: pick what stops, what happens to waiting leads, and how it comes back.
export function PauseAlertDialog({ alert, open, onOpenChange, onDone }) {
  const [scope, setScope] = useState("destination");
  const [waiting, setWaiting] = useState("hold");
  const [auto, setAuto] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery-alerts/${alert.id}/pause`, { scope, waiting, auto_resume: auto });
      toast.success(pauseSummary(data));
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't pause it");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pause-alert-dialog">
        <DialogHeader>
          <DialogTitle>Pause {alert.destination_name}</DialogTitle>
          <DialogDescription>New leads skip it and go to the next matching buyer. Leads from this buyer's own pages can't go to another firm (their consent names this buyer), so those wait.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="label-mono">What to pause</div>
          <Option on={scope === "destination"} onClick={() => setScope("destination")} testId="pause-scope-destination"
            title="Just this delivery place" sub="The buyer's other delivery places keep getting leads." />
          {alert.client_id && (
            <Option on={scope === "buyer"} onClick={() => setScope("buyer")} testId="pause-scope-buyer"
              title={`The whole buyer${alert.client_name ? ` (${alert.client_name})` : ""}`} sub="No new leads go to this buyer at all." />
          )}
          <div className="label-mono pt-2">Leads already waiting to retry</div>
          <Option on={waiting === "hold"} onClick={() => setWaiting("hold")} testId="pause-waiting-hold"
            title="Hold them until it's back on" sub="They go out to this buyer once you resume." />
          <Option on={waiting === "reroute"} onClick={() => setWaiting("reroute")} testId="pause-waiting-reroute"
            title="Send them to the next matching buyer now" sub="Any with no other buyer (or from this buyer's own pages) stay held." />
          <label className="flex items-center gap-2 pt-2 text-xs" data-testid="pause-auto-resume">
            <Checkbox checked={auto} onCheckedChange={(v) => setAuto(Boolean(v))} data-testid="pause-auto-resume-checkbox" />
            Turn back on by itself when a test send works
          </label>
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} data-testid="pause-cancel"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest transition-colors duration-100 hover:bg-zinc-100">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} data-testid="pause-confirm"
            className="border-2 border-red-700 bg-red-700 px-3 py-2 text-xs uppercase tracking-widest text-white transition-colors duration-100 hover:bg-red-800 disabled:opacity-40">
            {busy ? "Pausing…" : "Pause now"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
